import { Module } from 'vuex';
import api from '@/services/api';

const SESSION_STORAGE_KEY = 'suite241.cashRegisterSession';

export interface CashRegisterSessionState {
  currentSession: CashRegisterSession | null;
  isLoading: boolean;
}

export interface CashRegister {
  id: number;
  name: string;
  code?: string | null;
  type?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface CashRegisterSession {
  id: number;
  shop_id: number;
  cash_register_id: number;
  user_id: number;
  status: 'open' | 'closed';
  opening_amount: number | string;
  opened_at?: string | null;
  closed_at?: string | null;
  operation_date?: string | null;
  is_historical_entry?: boolean;
  user?: { id: number; name: string } | null;
  cash_register?: CashRegister | null;
  cashRegister?: CashRegister | null;
  resume_mode?: boolean;
  access_grant?: {
    id: number;
    expires_at?: string | null;
    reason?: string | null;
    status?: string | null;
  } | null;
}

const state: CashRegisterSessionState = {
  currentSession: null,
  isLoading: false,
};

function isResumedSessionActive(session: CashRegisterSession | null): boolean {
  if (!session?.resume_mode || session.status !== 'closed') {
    return false;
  }

  const expiresAt = session.access_grant?.expires_at;
  if (!expiresAt) {
    return false;
  }

  return new Date(expiresAt).getTime() > Date.now();
}

function isCashRegisterAutoCloseEnabled(rootGetters?: Record<string, unknown>): boolean {
  const shopInfo = rootGetters?.['shop/shopInfo'] as { cash_register_auto_close_enabled?: boolean } | null | undefined;

  return shopInfo?.cash_register_auto_close_enabled === true;
}

const cashRegisterModule: Module<CashRegisterSessionState, Record<string, unknown>> = {
  namespaced: true,
  state,
  getters: {
    currentSession(s): CashRegisterSession | null {
      return s.currentSession;
    },
    hasOpenSession(s): boolean {
      return !!s.currentSession && s.currentSession.status === 'open';
    },
    isResumedClosedSession(s): boolean {
      return isResumedSessionActive(s.currentSession);
    },
    isLoading(s): boolean {
      return s.isLoading;
    },
  },
  mutations: {
    SET_LOADING(s, loading: boolean) {
      s.isLoading = loading;
    },
    SET_SESSION(s, session: CashRegisterSession | null) {
      s.currentSession = session;

      if (session) {
        window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
      } else {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    },
  },
  actions: {
    LOAD_FROM_LOCALSTORAGE({ commit, rootGetters }) {
      const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return;

      try {
        const session = JSON.parse(raw);
        if (session?.status === 'open') {
          commit('SET_SESSION', session);
        } else if (isCashRegisterAutoCloseEnabled(rootGetters) && isResumedSessionActive(session)) {
          commit('SET_SESSION', session);
        } else {
          commit('SET_SESSION', null);
        }
      } catch (error) {
        commit('SET_SESSION', null);
      }
    },
    async fetchCurrent({ commit, state, rootGetters }) {
      commit('SET_LOADING', true);
      try {
        const response = await api.get('/cash-register-sessions/current');
        const data = response.data?.data || response.data || {};
        if (data.has_open_session) {
          commit('SET_SESSION', data.session);
          return data.session || null;
        }

        const resumedSession = state.currentSession;
        if (
          isCashRegisterAutoCloseEnabled(rootGetters)
          && resumedSession
          && isResumedSessionActive(resumedSession)
        ) {
          try {
            const resumeResponse = await api.post(`/cash-register-sessions/${resumedSession.id}/resume-closed`);
            const refreshedSession = resumeResponse.data?.data || resumeResponse.data;
            commit('SET_SESSION', refreshedSession);
            return refreshedSession;
          } catch (error) {
            commit('SET_SESSION', null);
            return null;
          }
        }

        commit('SET_SESSION', null);
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchAuthorizedClosed() {
      const response = await api.get('/cash-register-sessions/authorized-closed');
      return response.data?.data || response.data || [];
    },
    async resumeClosed({ commit }, sessionId: number) {
      const response = await api.post(`/cash-register-sessions/${sessionId}/resume-closed`);
      const session = response.data?.data || response.data;
      commit('SET_SESSION', session);
      return session;
    },
    leaveClosedResume({ commit }) {
      commit('SET_SESSION', null);
    },
    async open({ commit }, payload: { cash_register_id: number; opening_amount: number }) {
      const response = await api.post('/cash-register-sessions/open', payload);
      const session = response.data?.data || response.data;
      commit('SET_SESSION', session);
      return session;
    },
    async closeCurrent({ commit }) {
      const response = await api.post('/cash-register-sessions/current/close');
      const session = response.data?.data || response.data;
      commit('SET_SESSION', null);
      return session;
    },
    clear({ commit }) {
      commit('SET_SESSION', null);
    },
  },
};

export default cashRegisterModule;
